import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface Faq {
  readonly faqId: string;
  readonly question: string;
  readonly answer: string;
  readonly category: string | null;
}

/** M27 — FAQ Center (§31). Lists FAQs with a keyword filter and supports adding
 * new FAQs over faq.controller (KB_MANAGE). */
export function M27FaqCenterPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<Faq[]>([]);
  const [total, setTotal] = useState(0);
  const [term, setTerm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [category, setCategory] = useState('');

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    const q = term ? `?q=${encodeURIComponent(term)}` : '';
    try {
      const r = await apiRequest<{ data: Faq[]; total: number }>(`/v1/collab/faqs${q}`, { token });
      setRows(r.data); setTotal(r.total); setError(null);
    } catch (e) { setError((e as Error).message); }
    finally { setLoading(false); }
  }, [token, term]);

  useEffect(() => { void load(); }, [load]);

  async function create(): Promise<void> {
    if (!question.trim() || !answer.trim()) return;
    setBusy(true); setNotice(null);
    try {
      await apiRequest('/v1/collab/faqs', {
        token, method: 'POST',
        body: { question, answer, category: category.trim() || undefined },
      });
      setQuestion(''); setAnswer(''); setCategory('');
      setNotice(t('m27.faq.noticeAdded'));
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }

  return (
    <main aria-label="faq-center">
      <h1>{t('m27.faq.heading')}</h1>

      <form aria-label="faq-create-form" onSubmit={(e) => { e.preventDefault(); void create(); }}>
        <label htmlFor="faq-question">{t('m27.faq.fldQuestion')}</label>
        <input id="faq-question" value={question} onChange={(e) => setQuestion(e.target.value)} required />
        <label htmlFor="faq-answer">{t('m27.faq.fldAnswer')}</label>
        <textarea id="faq-answer" value={answer} onChange={(e) => setAnswer(e.target.value)} required />
        <label htmlFor="faq-category">{t('m27.faq.fldCategory')}</label>
        <input id="faq-category" value={category} onChange={(e) => setCategory(e.target.value)} />
        <button type="submit" disabled={busy}>{t('m27.faq.btnAdd')}</button>
      </form>

      <label htmlFor="faq-search">{t('m27.faq.fldSearch')}</label>
      <input id="faq-search" value={term} onChange={(e) => setTerm(e.target.value)} />
      {notice && <p role="status">{notice}</p>}
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m27.faq.loading')}</p> : (
        <>
          <p aria-label="faq-count">{t('m27.faq.count', { count: total })}</p>
          <dl aria-label="faq-list">
            {rows.map((f) => (<div key={f.faqId}><dt>{f.question}</dt><dd>{f.answer}</dd></div>))}
          </dl>
        </>
      )}
    </main>
  );
}
